import type { GeneratedApplicationProposal } from "@cogo/a2a-gateway/generated-application-proposal";
import { hashCanonicalContent } from "@cogo/schema/canonical-content-hash";
import type { ContentRef } from "@cogo/publisher-core";

import {
  ARTIFACT_REVISION_ENVELOPE_SCHEMA_VERSION,
  GENERATED_ARTIFACT_APPROVAL_DECISION_SCHEMA_VERSION,
  type ArtifactRevisionRef,
  type GeneratedArtifactApprovalDecision,
  type UncommittedArtifactRevisionEnvelope,
} from "./artifact-revision-envelope";
import { STUDIO_PROJECT_SERVER_REVISION_AUTHORITY } from "./authenticated-studio-generated-artifact-persistence";

export const GENERATED_ARTIFACT_PROPOSAL_REVISION_AUTHORITY =
  "generated-artifact-proposal" as const;

export interface ExistingProjectGeneratedArtifactCandidate {
  requestedByUserId: string;
  proposal: GeneratedApplicationProposal;
  envelope: UncommittedArtifactRevisionEnvelope & {
    target: { kind: "existing-project"; projectId: string };
  };
}

function nonEmpty(value: string, field: string): string {
  const normalized = value.trim();
  if (!normalized) throw new Error(`Generated artifact ${field} is unavailable.`);
  return normalized;
}

function canonicalProposalEvidenceRef(input: {
  projectId: string;
  artifactId: string;
  requestId: string;
  runId: string;
  contentHash: `sha256:${string}`;
}): ContentRef {
  return {
    ref: `cogo://studio/projects/${encodeURIComponent(input.projectId)}/generated-artifacts/${encodeURIComponent(input.artifactId)}/proposals/${encodeURIComponent(input.requestId)}/runs/${encodeURIComponent(input.runId)}`,
    contentHash: input.contentHash,
  };
}

function uniqueEvidenceRefs(refs: readonly ContentRef[]): ContentRef[] {
  const unique = new Map<string, ContentRef>();
  for (const ref of refs) {
    if (!ref.ref.trim() || !/^sha256:[a-f0-9]{64}$/.test(ref.contentHash)) {
      throw new Error("Generated artifact evidence is malformed.");
    }
    unique.set(`${ref.ref}\u0000${ref.contentHash}`, { ...ref });
  }
  return [...unique.values()];
}

/**
 * Closes one authenticated Chat generation into the candidate that the inline
 * approval card must carry unchanged. The parent is accepted only from the
 * Studio project authority; prompt text, a selected block, or local Zustand
 * state can never be promoted into lineage here.
 */
export function buildExistingProjectGeneratedArtifactCandidate(input: {
  proposal: GeneratedApplicationProposal;
  projectId: string;
  parentRevision: ArtifactRevisionRef;
  requestId: string;
  requestedByUserId: string;
  evidenceRefs?: readonly ContentRef[];
}): ExistingProjectGeneratedArtifactCandidate {
  const projectId = nonEmpty(input.projectId, "project id");
  const requestId = nonEmpty(input.requestId, "request id");
  const requestedByUserId = nonEmpty(
    input.requestedByUserId,
    "requesting user id",
  );
  const artifactId = nonEmpty(input.proposal.proposalId, "artifact id");
  const runId = nonEmpty(input.proposal.requestedBy.runId, "run id");
  const projectRevisionPrefix = `${projectId}@`;
  const projectRevisionNumber = input.parentRevision.value.startsWith(
    projectRevisionPrefix,
  )
    ? input.parentRevision.value.slice(projectRevisionPrefix.length)
    : "";
  const parsedProjectRevision = Number(projectRevisionNumber);
  if (
    input.parentRevision.authority !==
      STUDIO_PROJECT_SERVER_REVISION_AUTHORITY ||
    !/^\d+$/.test(projectRevisionNumber) ||
    !Number.isSafeInteger(parsedProjectRevision) ||
    parsedProjectRevision < 1 ||
    input.proposal.surface.baseRevision !== input.parentRevision.value
  ) {
    throw new Error(
      "Generated artifact proposal is not bound to the authenticated project revision.",
    );
  }

  const contentHash = hashCanonicalContent(input.proposal.surface.cogoJson);
  const evidenceRefs = uniqueEvidenceRefs([
    ...(input.evidenceRefs ?? []),
    canonicalProposalEvidenceRef({
      projectId,
      artifactId,
      requestId,
      runId,
      contentHash,
    }),
  ]);
  return {
    requestedByUserId,
    proposal: input.proposal,
    envelope: {
      schemaVersion: ARTIFACT_REVISION_ENVELOPE_SCHEMA_VERSION,
      artifactId,
      target: { kind: "existing-project", projectId },
      revision: {
        authority: GENERATED_ARTIFACT_PROPOSAL_REVISION_AUTHORITY,
        value: `${artifactId}@${contentHash}`,
      },
      parentRevision: { ...input.parentRevision },
      contentHash,
      requestId,
      runId,
      evidenceRefs,
      persistenceReceipt: null,
    },
  };
}

export function buildGeneratedArtifactApprovalDecision(input: {
  candidate: ExistingProjectGeneratedArtifactCandidate;
  actorId: string;
  decision: "approved" | "rejected";
  decisionId?: string;
}): GeneratedArtifactApprovalDecision {
  const actorId = nonEmpty(input.actorId, "approval actor");
  const envelope = input.candidate.envelope;
  return {
    schemaVersion: GENERATED_ARTIFACT_APPROVAL_DECISION_SCHEMA_VERSION,
    decisionId: nonEmpty(
      input.decisionId ?? crypto.randomUUID(),
      "approval decision id",
    ),
    artifactId: envelope.artifactId,
    requestId: envelope.requestId,
    runId: envelope.runId,
    actorId,
    actorKind: "human",
    decision: input.decision,
    target: { ...envelope.target },
    revision: { ...envelope.revision },
    parentRevision: envelope.parentRevision
      ? { ...envelope.parentRevision }
      : null,
    contentHash: envelope.contentHash,
  };
}
